<template>
  <div v-if="showUserWatchBtn" :class="[$style.userWatchBtn, userWatchData ? $style.hasUserWatch : '']">
    <spacer
      v-if="!flag.loadFinished"
      :y="2.5"
    />
    <loader-simple :flag="!flag.loadFinished">
      <div
        v-if="flag.loadFinished"
        :class="$style.userWatchBtnIndicator"
      >

        <div :class="$style.userWatchBtns">
          <basic-btn
            tag="button"
            addClass="risk"
            v-on:click="clickBtn"
            :disabled="btnDisabled">２週間アラート</basic-btn>
        </div>

        <div>
          <p :class="$style.userWatchStatus">
            <template v-if="userWatchData">
              <i class="fa-regular fa-flag-alt" /><span>フラグ設定</span>
              <span>（現在の担当：{{ mainCounselor.username }}　{{ this.helper.master.labels.users.account_type[mainCounselor.account_type] }}）</span>
            </template>
            <template v-else>
              <span>設定なし</span>
            </template>
          </p>
        </div>
      </div>
    </loader-simple>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import LoaderSimple from '@/views/components/LoaderSimple.vue';
import Spacer from '@/views/components/Spacer.vue';
import BasicBtn from '@/views/components/BasicBtn.vue';

import moment from 'moment';

export default {
  name: 'user-watch-btn',
  components: {
    LoaderSimple,
    Spacer,
    BasicBtn,
  },
  props: {
    customer: {
      type: Object,
      required: true,
    },
    counseling_type: {
      type: String,
      required: false, // ユーザ一覧から呼ばれた場合は不要
      validator: (value) => ['1', '2', '3'].includes(value), // 1: オンライン, 2: テキスト, 3: バーチャル
    },
    counseling_data: {
      type: Object,
      required: false, // ユーザ一覧から呼ばれた場合は不要
    },
  },
  data() {
    return {
      flag: {
        loadFinished: false,
      },
      userWatchData: null,
      mainCounselor: null, // メインカウンセラーの情報
      lastCounselingType: null, // 最後のカウンセリングのタイプ(1: オンライン, 2: テキスト, 3: バーチャル)
      lastCounselingId: null, // 最後のカウンセリングのID
      lastCounselingData: null, // 最後のカウンセリングのデータ
    };
  },
  computed: {
    ...mapState(['user', 'helper']),
    isOperator() {
      return this.user && this.user.account_type === 31; // 事務局
    },
    isMyClient() {
      if (!this.mainCounselor) {
        return false;
      }
      return this.mainCounselor.id === this.user.id;
    },
    isLastCounseling() {
      // 最後のカウンセリングであればtrueを返す
      return Number(this.counseling_type) === this.lastCounselingType && this.counseling_data.id === this.lastCounselingId;
    },
    isCounselingAfterWatch() {
      if (!this.userWatchData) {
        return false;
      }
      return moment(this.counseling_data.created_at).isSameOrAfter(moment(this.userWatchData.watched_at));
    },
    showUserWatchBtn() {
      // 完了状態の健康相談が1件も存在しない場合、表示しない
      if (!this.lastCounselingData) {
        return false;
      }
      // ログインユーザが医師心理、または事務局でなければ表示しない
      const allowedAccountTypes = [21, 22, 31];
      if (!this.user || !allowedAccountTypes.includes(this.user.account_type)) {
        return false;
      }
      // ログインユーザが事務局の場合の特殊判断
      if (this.isOperator) {
        // ユーザ一覧から呼ばれた場合は表示する
        if (!this.counseling_type && !this.counseling_data) {
          return true;
        }
      }
      // 自分が主担当の場合は表示する
      if (this.isMyClient) {
        return true;
      }
      // 自分が主担当でなくとも、ウォッチデータがあれば（すでに２週間アラートフラグが立っている場合は）表示する
      if (this.userWatchData) {
        return true;
      }
      // 上記以外は表示しない
      return false;
    },
    btnDisabled() {
      // 自分が事務局の場合はボタンを有効にする
      if (this.isOperator) {
        return false;
      }
      // 自分が主担当の場合はボタンを有効にする
      if (this.isMyClient) {
        return false;
      }
      // 自分が事務局の場合はボタンを有効にする
      if (this.user.account_type === 31) {
        return false;
      }
      // それ以外の場合はボタンを無効にする
      return true;
    },
  },
  created() {
    this.initialize();
  },
  methods: {
    async initialize() {
      let response;
      try {
        // ユーザウォッチデータを取得する
        response = await this.axios({
          method: 'GET',
          url: '/v1/userWatch/get',
          params: {
            user_id: this.customer.id,
          },
        });
        this.userWatchData = response.data.data;

        // 対象者の主担当カウンセラー情報を取得
        response = await this.axios({
          method: 'GET',
          url: '/v1/userWatch/get/mainCounselor',
          params: { user_id: this.customer.id },
        });
        if (response.data.data) {
          this.mainCounselor = response.data.data.main_counselor;
          this.lastCounselingType = response.data.data.last_counseling_type;
          this.lastCounselingId = response.data.data.last_counseling_id;
          this.lastCounselingData = response.data.data.last_counseling_data;
        }

        this.flag.loadFinished = true; // ロード完了フラグを立てる

      } catch (error) {
        console.error('Failed to fetch user watch data:', error);
      }
    },
    clickBtn() {
      if (!this.isMyClient && !this.isOperator) {
        return;
      }
      const msg = this.userWatchData ? `${this.customer.username}さんのアラート設定を解除します。よろしいですか？` : `${this.customer.username}さんをアラート対象に設定します。よろしいですか？`;
      if (!confirm(msg)) {
        return;
      }
      this.setUserWatch();
    },
    async setUserWatch() {
      let updatedData;
      try {
        if (this.userWatchData) {
          // delete（OFFにする）
          await this.axios({
            method: 'POST',
            url: '/v1/userWatch/set/delete',
            data: {
              user_id: this.customer.id,
            },
          });
          updatedData = this.userWatchData;
        } else {
          // insert（ONにする）

          // watched_atを健康相談の登録日時にする
          const targetCounselingData = this.counseling_data // 第一候補: 現在のカウンセリングデータ
            || this.lastCounselingData
            ? this.lastCounselingData // 第二候補: 最後のカウンセリングデータ
            : { created_at: moment().format('YYYY-MM-DD HH:mm:ss') }; // 第三候補: 現在の日時

          updatedData = {
            user_id: this.customer.id,
            is_watched: 1,
            watcher_id: this.user.id,
            watched_at: targetCounselingData.created_at,
          };

          await this.axios({
            method: 'POST',
            url: '/v1/userWatch/set/register',
            data: updatedData,
          });
        }

        // 親コンポーネントに変更を通知する
        this.$emit('update-user-watch-data', updatedData);
      } catch (e) {
        console.error(e);
        alert('２週間アラートの設定に失敗しました');
      }

      // 自身の情報を更新する
      this.initialize();
    },
  },
};
</script>

<style lang="scss" module>
.userWatchBtn {
  // userWatchデータがある場合は赤下線を引く
  &.hasUserWatch {
    border-bottom: 1px solid red;
  }
}
.userWatchBtnIndicator {
  margin-top: 2em;
  display: flex;
  align-items: center;
  .status {
    margin-left: 1em;
  }
}
.userWatchBtns {
  > button {
    display: block !important;
    &:not(:first-child) {
      margin-top: .5rem
    }
  }
}
.userWatchStatus {
  margin-left: 1rem;
  display: flex;
  align-items: center;
  > i {
    font-size: 2rem;
    margin-right: .5rem;
    color: red;
  }
}
</style>
